import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Box,
  Typography,
  Avatar,
  FormHelperText,
} from '@mui/material';
import {
  Person,
  Business,
} from '@mui/icons-material';
import { Client } from '../../types';
import { clientService } from '../../services/clientService';

interface ClientSelectProps {
  value?: number | string | null;
  onChange: (clientId: number | null, client?: Client) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  onlyActive?: boolean;
  allowClear?: boolean;
}

const ClientSelect: React.FC<ClientSelectProps> = ({
  value = null,
  onChange,
  label = 'Cliente',
  placeholder = 'Digite para buscar um cliente',
  required = false,
  disabled = false,
  error = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  variant = 'outlined',
  onlyActive = true,
  allowClear = true,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Buscar clientes baseado no input
  const searchClients = async (searchTerm: string = '') => {
    try {
      setLoading(true);
      setLoadError(null);

      const filters: any = {
        limit: 50, // Limitar resultados para performance
      };

      if (onlyActive) {
        filters.status = 'ativo';
      }

      // Se há termo de busca, adicionar aos filtros
      if (searchTerm.trim()) {
        // O backend deve aceitar um parâmetro search para buscar por nome ou CPF/CNPJ
        filters.search = searchTerm.trim();
      }

      const response = await clientService.getAll(filters);

      if (response.success && response.data) {
        setClients(response.data);
      } else {
        setLoadError('Erro ao carregar clientes');
        setClients([]);
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setLoadError('Erro ao buscar clientes');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // Carregar clientes iniciais
  useEffect(() => {
    searchClients();
  }, [onlyActive]);

  // Buscar quando o input muda (com debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue.length >= 2 || inputValue.length === 0) {
        searchClients(inputValue);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  // Encontrar cliente selecionado baseado no value (apenas quando value muda externamente)
  useEffect(() => {
    if (value) {
      const clientId = typeof value === 'string' ? parseInt(value) : value;
      
      // Se já temos o cliente selecionado correto, não fazer nada
      if (selectedClient && selectedClient.id === clientId) {
        return;
      }
      
      // Tentar encontrar na lista atual
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setSelectedClient(client);
      }
      // Se não encontrou na lista, manter o selectedClient atual se o ID coincide
      // Isso evita que o botão X desapareça quando a lista muda
    } else if (value === null) {
      // Apenas limpar se o value foi explicitamente definido como null
      setSelectedClient(null);
    }
  }, [value]); // Removido 'clients' e 'selectedClient' das dependências para evitar loops

  const handleChange = (_: any, newValue: Client | null) => {
    setSelectedClient(newValue);
    onChange(newValue?.id || null, newValue || undefined);
    
    // Se um cliente foi selecionado, garantir que ele está na lista de opções
    if (newValue) {
      const existsInList = clients.some(c => c.id === newValue.id);
      if (!existsInList) {
        // Adicionar o cliente selecionado à lista para garantir que o botão X funcione
        setClients(prev => {
          const alreadyExists = prev.some(c => c.id === newValue.id);
          return alreadyExists ? prev : [...prev, newValue];
        });
      }
    }
    
    // Se o valor foi limpo, resetar o inputValue também
    if (!newValue) {
      setInputValue('');
      // Recarregar lista inicial quando limpar
      searchClients('');
    }
  };

  const formatClientOption = (client: Client) => {
    const isPJ = client.tipo_pessoa === 'pj';
    return `${client.nome} - ${client.cpf_cnpj}`;
  };

  const getClientIcon = (client: Client) => {
    return client.tipo_pessoa === 'pj' ? <Business /> : <Person />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'success';
      case 'inativo':
        return 'default';
      case 'suspenso':
        return 'warning';
      case 'inadimplente':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'Ativo';
      case 'inativo':
        return 'Inativo';
      case 'suspenso':
        return 'Suspenso';
      case 'inadimplente':
        return 'Inadimplente';
      default:
        return status;
    }
  };

  return (
    <FormControl 
      fullWidth={fullWidth} 
      error={error}
      disabled={disabled}
      size={size}
    >
      {loadError && (
        <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem' }}>
          {loadError}
        </Alert>
      )}
      
      <Autocomplete
        value={selectedClient}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          setInputValue(newInputValue);
          // Se o usuário apagou tudo manualmente (backspace/delete) e havia um cliente selecionado
          if (reason === 'input' && newInputValue.length === 0 && selectedClient) {
            // Limpar a seleção também
            handleChange(null, null);
          }
        }}
        options={clients}
        getOptionLabel={(option) => formatClientOption(option)}
        loading={loading}
        disabled={disabled}
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        freeSolo={false}
        disableClearable={!allowClear}
        filterOptions={(x) => x} // Não filtrar aqui, já filtramos no backend
        isOptionEqualToValue={(option, value) => {
          if (!value || !option) return false;
          return option.id === value.id;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label + (required ? ' *' : '')}
            placeholder={placeholder}
            variant={variant}
            size={size}
            error={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                {getClientIcon(option)}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
                  {option.nome}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                  {option.cpf_cnpj} • {option.cidade}/{option.estado}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                <Chip
                  size="small"
                  label={getStatusLabel(option.status)}
                  color={getStatusColor(option.status) as any}
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.65rem' }}
                />
                <Chip
                  size="small"
                  label={option.tipo_pessoa === 'pj' ? 'PJ' : 'PF'}
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.65rem' }}
                />
              </Box>
            </Box>
          </Box>
        )}
        noOptionsText={
          inputValue.length < 2 
            ? "Digite pelo menos 2 caracteres para buscar"
            : loading 
              ? "Buscando..."
              : "Nenhum cliente encontrado"
        }
      />
      
      {helperText && (
        <FormHelperText sx={{ fontSize: '0.75rem' }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default ClientSelect; 